import { useState } from "react";
import axios from "axios";
import { API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Bell, CheckCircle2, Loader2 } from "lucide-react";

const STRATEGIES = [
  { v: "wholesale", l: "Wholesale" },
  { v: "flip", l: "Fix & Flip" },
  { v: "brrrr", l: "BRRRR" },
  { v: "rental", l: "Buy & Hold" },
  { v: "novation", l: "Novation" },
  { v: "sub_to", l: "Sub-To" },
  { v: "seller_finance", l: "Seller Finance" },
];

/**
 * BuyerSignupPanel — embedded on the public Deal Room page. Lets a buyer opt
 * into that wholesaler's deal-alerts list without leaving the page. Pre-seeds
 * criteria from the current deal (same state / one strategy) so signup is 3
 * clicks, not a form marathon.
 */
export default function BuyerSignupPanel({ slug, accent, defaultState, defaultStrategy }) {
  const [expanded, setExpanded] = useState(false);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [states, setStates] = useState(defaultState ? [defaultState.toUpperCase()] : []);
  const [priceMax, setPriceMax] = useState("");
  const [strategies, setStrategies] = useState(defaultStrategy ? [defaultStrategy] : []);
  const [notifySms, setNotifySms] = useState(false);

  const toggleStrategy = (v) => setStrategies((s) => (s.includes(v) ? s.filter(x => x !== v) : [...s, v]));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/public/buyers/join`, {
        workspace_slug: slug,
        email,
        name,
        phone: phone || undefined,
        criteria: {
          states: states.length ? states : undefined,
          price_max: priceMax ? Number(priceMax) : undefined,
          strategies: strategies.length ? strategies : undefined,
          notify_sms: notifySms || undefined,
        },
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-xl border border-[var(--line,#E2E8F0)] bg-white p-6 text-center" data-testid="buyer-signup-success">
        <CheckCircle2 size={36} className="text-emerald-600 mx-auto mb-2" />
        <div className="font-display font-bold text-lg">You&apos;re on the list ✅</div>
        <div className="text-sm text-[var(--ink-2,#475569)] mt-1">
          Check your inbox — we sent you a confirmation email with a link to tune your alert criteria any time.
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border-2 border-dashed p-5" style={{ borderColor: accent, background: "#FFFDF7" }} data-testid="buyer-signup-panel">
      <div className="flex items-start gap-3 mb-3">
        <div className="rounded-full p-2 shrink-0" style={{ background: accent }}>
          <Bell size={18} color="#0F172A" />
        </div>
        <div>
          <div className="font-display font-bold text-base leading-tight">Get alerted the moment a deal like this drops.</div>
          <div className="text-xs text-[var(--ink-2,#475569)] mt-0.5">
            Free · email only · unsubscribe with one click.
          </div>
        </div>
      </div>

      {!expanded ? (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="w-full py-2.5 rounded-lg font-semibold text-sm border border-[var(--ink,#0F172A)] text-[var(--ink,#0F172A)] hover:bg-[var(--ink,#0F172A)] hover:text-white transition"
          data-testid="buyer-signup-expand"
        >
          Join the list →
        </button>
      ) : (
        <form onSubmit={submit} className="space-y-2.5" data-testid="buyer-signup-form">
          <input required placeholder="Your name *" value={name} onChange={(e) => setName(e.target.value)} className="input text-sm" data-testid="buyer-signup-name" />
          <input required type="email" placeholder="Email *" value={email} onChange={(e) => setEmail(e.target.value)} className="input text-sm" data-testid="buyer-signup-email" />
          <input placeholder="Phone (optional)" value={phone} onChange={(e) => setPhone(e.target.value)} className="input text-sm" data-testid="buyer-signup-phone" />
          <div className="grid grid-cols-2 gap-2">
            <input
              placeholder="Target state(s)"
              value={states.join(", ")}
              onChange={(e) => setStates(e.target.value.split(",").map(s => s.trim().toUpperCase()).filter(Boolean))}
              className="input text-sm"
              data-testid="buyer-signup-states"
            />
            <input
              type="number"
              placeholder="Max price $"
              value={priceMax}
              onChange={(e) => setPriceMax(e.target.value)}
              className="input text-sm"
              data-testid="buyer-signup-pricemax"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3,#94A3B8)] mb-1">Interested in</div>
            <div className="flex flex-wrap gap-1.5">
              {STRATEGIES.map((s) => {
                const active = strategies.includes(s.v);
                return (
                  <button
                    key={s.v}
                    type="button"
                    onClick={() => toggleStrategy(s.v)}
                    className={`text-[11px] font-semibold px-2 py-1 rounded-full border transition ${active ? "bg-[var(--ink,#0F172A)] text-white border-[var(--ink,#0F172A)]" : "bg-white text-[var(--ink-2,#475569)] border-[var(--line,#E2E8F0)]"}`}
                    data-testid={`buyer-signup-strategy-${s.v}`}
                  >{s.l}</button>
                );
              })}
            </div>
          </div>
          <label className="flex items-start gap-2 text-xs text-[var(--ink-2,#475569)] leading-relaxed pt-1">
            <input
              type="checkbox"
              checked={notifySms}
              onChange={(e) => setNotifySms(e.target.checked)}
              disabled={!phone}
              className="mt-0.5 h-4 w-4 rounded border-[var(--line,#E2E8F0)] accent-[var(--ink,#0F172A)] shrink-0"
              data-testid="buyer-signup-sms"
            />
            <span>
              Also text me when a matching deal drops.{" "}
              {!phone && <span className="text-[var(--ink-3,#94A3B8)]">(Add phone above to enable)</span>}
              {phone && <span className="text-[var(--ink-3,#94A3B8)]"> Msg&amp;data rates may apply · reply STOP to opt out.</span>}
            </span>
          </label>
          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg font-semibold text-sm"
            style={{ background: accent, color: "#0F172A" }}
            data-testid="buyer-signup-submit"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Bell size={14} />}
            {loading ? "Signing you up…" : "Notify me of matching deals"}
          </button>
          <p className="text-[10px] text-[var(--ink-3,#94A3B8)] leading-relaxed">
            By signing up you agree to receive email alerts. No SMS in this beta. Unsubscribe any time from your dashboard link (sent by email).
          </p>
        </form>
      )}
    </div>
  );
}
